<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ChevronLeft, ChevronRight, Search, SlidersHorizontal } from 'lucide-vue-next'
import { getCourseCategories, getCourses, getPublicCourses } from '../services/api'
import { fallbackCourses } from '../data/fallback'

const route = useRoute()
const router = useRouter()
const courses = ref([])
const categories = ref([])
const keyword = ref(String(route.query.q || ''))
const activeCategoryId = ref('')
const loading = ref(true)
const publicCourseMap = ref(new Map())
const currentPage = ref(1)
const pageSize = 8
let loadVersion = 0

const categoryOptions = computed(() => [{ id: '', title: '全部' }, ...categories.value])
const activeCategory = computed(() => categoryOptions.value.find(item => String(item.id) === activeCategoryId.value) || categoryOptions.value[0])
const filtered = computed(() => courses.value.filter(item => {
  const text = `${item.title || ''}${item.lights || item.desc || ''}${item.cateName || item.cat || ''}`.toLowerCase()
  return !keyword.value || text.includes(keyword.value.toLowerCase())
}))
const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / pageSize)))
const paginatedCourses = computed(() => {
  const start = (currentPage.value - 1) * pageSize
  return filtered.value.slice(start, start + pageSize)
})
const cover = item => item.images || '/assets/logo.png'
const isOwned = item => Number(item.orderFlag) === 1 || item.purchased === true
const accessLabel = item => isOwned(item) ? '已购买' : item.vipAccess ? '会员可学' : Number(item.freeType) === 0 ? '免费' : ''
const canLearn = item => Boolean(item.accessGranted || isOwned(item))

watch([keyword, activeCategoryId], () => { currentPage.value = 1 })

function submit() {
  router.replace({ query: keyword.value ? { q: keyword.value } : {} })
}

async function loadCourses(categoryId = activeCategoryId.value) {
  const version = ++loadVersion
  loading.value = true
  try {
    const result = await getCourses(categoryId ? { cateId: categoryId } : {})
    if (version !== loadVersion) return
    courses.value = result.map(item => ({ ...publicCourseMap.value.get(String(item.id)), ...item }))
  } catch {
    if (version !== loadVersion) return
    courses.value = categoryId ? [] : fallbackCourses
  } finally {
    if (version === loadVersion) loading.value = false
  }
}

function changeCategory(category) {
  const categoryId = String(category.id ?? '')
  if (categoryId === activeCategoryId.value) return
  activeCategoryId.value = categoryId
  loadCourses(categoryId)
}

function changePage(page) {
  currentPage.value = Math.min(Math.max(1, page), totalPages.value)
  document.querySelector('.course-library')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

onMounted(async () => {
  const [categoryRows, publicCourses] = await Promise.all([
    getCourseCategories().catch(() => []),
    getPublicCourses().catch(() => [])
  ])
  categories.value = categoryRows
  publicCourseMap.value = new Map(publicCourses.map(item => [String(item.id), item]))
  await loadCourses()
})
</script>

<template>
  <main>
    <section class="page-hero compact-hero">
      <div class="container">
        <p class="eyebrow">COURSE LIBRARY</p>
        <h1>找到下一门值得学的课</h1>
        <p>从入门方法到平台运营，按你的业务阶段选择。</p>
        <form class="library-search" @submit.prevent="submit">
          <Search :size="20" />
          <input v-model.trim="keyword" aria-label="搜索课程" placeholder="搜索课程、平台或主题" />
          <button class="button primary" type="submit">搜索</button>
        </form>
      </div>
    </section>

    <section class="section">
      <div class="container course-library">
        <div class="filter-head">
          <div><p class="eyebrow">COURSE CATEGORY</p><h2>{{ activeCategory.title === '全部' ? '全部课程' : activeCategory.title }}</h2></div>
          <span class="result-count"><SlidersHorizontal :size="16" /> {{ filtered.length }} 门</span>
        </div>
        <div class="category-tabs" aria-label="课程分类">
          <button
            v-for="category in categoryOptions"
            :key="String(category.id)"
            type="button"
            :class="{ active: String(category.id) === activeCategoryId }"
            :aria-pressed="String(category.id) === activeCategoryId"
            @click="changeCategory(category)"
          >{{ category.title }}</button>
        </div>
        <div v-if="loading" class="course-grid"><div v-for="i in 8" :key="i" class="course-card skeleton"></div></div>
        <div v-else-if="filtered.length" class="course-grid">
          <article v-for="course in paginatedCourses" :key="course.id" class="course-card library-card">
            <RouterLink :to="`/courses/${course.id}`" class="course-cover"><img :src="cover(course)" :alt="course.title" /></RouterLink>
            <div class="course-body">
              <p class="course-category">{{ course.cateName || course.cat || '精品课程' }}</p>
              <h3><RouterLink :to="`/courses/${course.id}`">{{ course.title }}</RouterLink></h3>
              <p class="course-description">{{ course.lights || course.desc || '系统课程，支持多端学习。' }}</p>
              <div class="course-foot">
                <strong v-if="accessLabel(course)" class="owned">{{ accessLabel(course) }}</strong>
                <strong v-else-if="Number(course.price) > 0">¥{{ Number(course.price).toFixed(0) }}</strong>
                <strong v-else>免费</strong>
                <RouterLink :to="`/courses/${course.id}`">{{ canLearn(course) ? '继续学习' : '立即试看' }}</RouterLink>
              </div>
            </div>
          </article>
        </div>
        <div v-else class="empty-state"><h3>没有找到对应课程</h3><p>换一个关键词或分类试试。</p></div>
        <nav v-if="!loading && filtered.length > pageSize" class="pagination" aria-label="课程分页">
          <button type="button" :disabled="currentPage === 1" aria-label="上一页" @click="changePage(currentPage - 1)"><ChevronLeft :size="18" /></button>
          <button
            v-for="page in totalPages"
            :key="page"
            type="button"
            :class="{ active: currentPage === page }"
            :aria-current="currentPage === page ? 'page' : undefined"
            :aria-label="`第 ${page} 页`"
            @click="changePage(page)"
          >{{ page }}</button>
          <button type="button" :disabled="currentPage === totalPages" aria-label="下一页" @click="changePage(currentPage + 1)"><ChevronRight :size="18" /></button>
        </nav>
      </div>
    </section>
  </main>
</template>

<style scoped>
.compact-hero{padding:76px 0 54px}.compact-hero h1{max-width:760px}.library-search{display:flex;align-items:center;gap:12px;max-width:700px;margin-top:30px;padding:8px 8px 8px 18px;background:var(--surface);border:1px solid var(--line);border-radius:12px}.library-search input{flex:1;min-width:0;border:0;outline:0;background:transparent;color:var(--text);font:inherit}.filter-head,.course-foot{display:flex;align-items:center;justify-content:space-between;gap:16px}.filter-head h2{margin:5px 0 0}.result-count{display:flex;gap:7px;align-items:center;color:var(--muted)}.category-tabs{display:flex;gap:8px;overflow:auto;margin:24px 0 28px;padding-bottom:4px}.category-tabs button{white-space:nowrap;padding:9px 15px;border:1px solid var(--line);border-radius:999px;background:var(--surface);color:var(--muted);cursor:pointer}.category-tabs button.active{background:var(--brand);border-color:var(--brand);color:#fff}.library-card{min-height:100%}.course-cover{aspect-ratio:16/10;overflow:hidden;background:var(--soft)}.course-cover img{width:100%;height:100%;object-fit:cover}.course-body{padding:19px}.course-category{font-size:12px;letter-spacing:.08em;color:var(--brand);font-weight:750}.course-body h3{margin:8px 0;font-size:19px}.course-description{min-height:48px;color:var(--muted);font-size:14px;line-height:1.7}.course-foot{margin-top:17px;padding-top:15px;border-top:1px solid var(--line)}.course-foot a{color:var(--brand);font-weight:750}.owned{color:#16835b}.empty-state{text-align:center;padding:80px 20px;background:var(--surface);border:1px solid var(--line);border-radius:16px}@media(max-width:700px){.library-search .button{padding-inline:16px}.filter-head{align-items:flex-end}}
</style>

<style scoped>
/* Uses the same 800 x 1000 cover uploaded for the mini program. */
.course-cover{aspect-ratio:4/5;background:transparent}
.course-cover img{width:100%;height:100%;display:block;object-fit:cover}
.course-body{min-height:220px}
.course-description{display:-webkit-box;min-height:48px;overflow:hidden;-webkit-box-orient:vertical;-webkit-line-clamp:2}
</style>
<style scoped>
.compact-hero{padding:88px 0 62px}.compact-hero h1{max-width:820px;font-size:clamp(46px,7vw,82px);line-height:.94;letter-spacing:-.065em}.library-search{border-radius:3px;box-shadow:none;border-color:var(--line-strong)}.category-tabs{gap:0;border-bottom:1px solid var(--line)}.category-tabs button{border-radius:0;padding:12px 18px;border:0;border-bottom:2px solid transparent;background:transparent}.category-tabs button.active{background:transparent;color:var(--blue);border-bottom-color:var(--blue)}.empty-state{border-radius:3px;box-shadow:none}
.container.course-library .course-grid{width:100%;grid-template-columns:repeat(4,minmax(0,1fr));gap:20px}@media(max-width:960px){.container.course-library .course-grid{width:100%;grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:720px){.container.course-library .course-grid{width:100%;grid-template-columns:1fr}}
.container.course-library{width:min(var(--max),calc(100% - 56px))}.course-library{scroll-margin-top:96px}.course-library .course-grid{width:100%;margin-inline:0;grid-template-columns:repeat(4,minmax(0,1fr));gap:20px;border:0;background:transparent}.course-library .course-card{border:1px solid var(--line)}.course-library .course-body{min-height:168px;padding:14px}.course-library .course-category{margin:0;font-size:10px}.course-library .course-body h3{margin:6px 0;font-size:16px;line-height:1.32}.course-library .course-description{min-height:40px;margin:0;font-size:12px;line-height:1.55}.course-library .course-foot{margin-top:12px;padding-top:11px;font-size:12px}.course-library .course-foot strong{font-size:13px}.pagination{margin-top:42px;display:flex;align-items:center;justify-content:center;gap:8px}.pagination button{width:42px;height:42px;display:grid;place-items:center;border:1px solid var(--line);border-radius:2px;background:var(--surface);color:var(--ink);font-weight:750}.pagination button:hover:not(:disabled){border-color:var(--blue);color:var(--blue)}.pagination button.active{border-color:var(--navy);background:var(--navy);color:#fff}.pagination button:disabled{cursor:not-allowed;opacity:.38}@media(max-width:960px){.course-library .course-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:720px){.container.course-library{width:min(100% - 36px,var(--max))}.course-library .course-grid{width:100%;grid-template-columns:1fr;gap:14px}.pagination{gap:5px;overflow-x:auto;justify-content:flex-start;padding-bottom:4px}.pagination button{width:40px;height:40px;flex:0 0 40px}}
</style>
